// Enhanced React Frontend with Modular Architecture
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './ButtonNew.css';
// Then in your App.tsx, import and use:
import { VERSION_INFO } from './constants/version';

import CollapsibleCell from './components/common/CollapsibleCell';
import SettingsDrawer from './components/modals/SettingsDrawer';

// Import all types from centralized types file
import {
    TestStatus,
    TestType,
    AIModel,
    ServiceStatus,
    AIConfig,
    AgentProfile,
    TestCaseDetails,
    TestResult
} from './types';

// Import modular components
import NavigationButtons from './components/widgets/NavigationButtons';
import AIConfigModal from './components/modals/AIConfigModal';
import ExecuteModal from './components/modals/ExecuteModal';
import TestDetailsModal from './components/modals/TestDetailsModal';
import DbViewerModal from './components/modals/DbViewerModal';
import DataEditorModal from './components/modals/DataEditorModal';

// Import view components
import DesignStudioView from './components/views/DesignStudioView';
import DesignStudioViewNew from './components/views/DesignStudioViewNew';//import ExecutionView from './components/views/ExecutionView';
//import ConfigView from './components/views/ConfigView';

//import AgentManagementView from './components/views/AgentManagementView';
import AgentTerminalView from './components/views/AgentTerminalView';

import LoginScreen from './components/auth/LoginScreen';
import WidgetDashboard from './components/auth/WidgetDashboard';

import AgentUploadModal from './components/modals/AgentUploadModal';
import TemplateLibraryModal from './components/modals/TemplateLibraryModal';

import AdminConsole from './components/auth/AdminConsole';
import EnvironmentWidget from './components/widgets/EnvironmentWidget';
import AnalyticsDashboardModal from './components/modals/AnalyticsDashboardModal';
import GeneratorModal from './components/modals/GeneratorModal';

import AIConfirmationDialog from './components/auth/AIConfirmationDialog';
import { showSuccess, TestWerkToasts } from './utils/toastSystem';


// Environment-aware API configuration
const getApiBaseUrl = () => {
    // First check if explicitly set (for Docker and GCP)
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }

    // Development fallback
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8000";
    }

    // Production: use hostname replacement only as last resort
    const hostname = window.location.hostname;

    // Check if we're on localhost (production build running locally)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8000";
    }

    // GCP production - replace frontend-alpha with orchestrator
    const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
    return `https://${apiHost}`;
};

const getWebSocketUrl = () => {
    if (process.env.NODE_ENV === 'development') {
        return process.env.REACT_APP_WS_URL || 'ws://localhost:8004';
    }
    return process.env.REACT_APP_WS_URL || 'wss://alpha.testwerk.ai/ws';
};

const API_BASE_URL = getApiBaseUrl();
const WS_URL = getWebSocketUrl();

// Analytics View Component
const AnalyticsView = () => (
    <div className="view-container">
        <div className="widget-grid">
            <div className="dashboard-widget">
                <h3>AI Performance Metrics</h3>
                <p>Comprehensive analysis of AI-driven testing performance and accuracy.</p>
                <div className="metric-display"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1.5rem' }}>
                    <div className="metric" style={{
                        textAlign: 'center',
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                        borderRadius: '8px'
                    }}>
                        <span className="metric-value" style={{
                            display: 'block',
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: '#155724'
                        }}>94%</span>
                        <span className="metric-label" style={{ fontSize: '0.9rem', color: '#155724' }}>AI Validation Accuracy</span>
                    </div>
                    <div className="metric" style={{
                        textAlign: 'center',
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #cce5ff 0%, #b3d9ff 100%)',
                        borderRadius: '8px'
                    }}>
                        <span className="metric-value" style={{
                            display: 'block',
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: '#0c5460'
                        }}>0.87</span>
                        <span className="metric-label"
                            style={{ fontSize: '0.9rem', color: '#0c5460' }}>Avg AI Confidence</span>
                    </div>
                    <div className="metric" style={{
                        textAlign: 'center',
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                        borderRadius: '8px'
                    }}>
                        <span className="metric-value" style={{
                            display: 'block',
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: '#856404'
                        }}>2.3s</span>
                        <span className="metric-label"
                            style={{ fontSize: '0.9rem', color: '#856404' }}>Avg Response Time</span>
                    </div>
                </div>
            </div>
            <div className="dashboard-widget">
                <h3>AI Model Performance</h3>
                <p>Token usage, cost analysis, and model performance comparison.</p>
                <div style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>GPT-4 Usage:</span>
                        <span style={{ fontWeight: 'bold' }}>15,432 tokens</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Estimated Cost:</span>
                        <span style={{ fontWeight: 'bold', color: '#dc2626' }}>$23.15</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Cache Hit Rate:</span>
                        <span style={{ fontWeight: 'bold', color: '#16a34a' }}>78%</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

function App() {
    // Core application state
    const [status, setStatus] = useState<ServiceStatus | null>(null);
    const [aiConfig, setAiConfig] = useState<AIConfig | null>(null);
    const [testCases, setTestCases] = useState<string[]>([]);
    const [selectedTest, setSelectedTest] = useState<string>('');
    const [results, setResults] = useState<Map<string, TestResult>>(new Map());
    const [isTestRunning, setIsTestRunning] = useState(false);
    const [isGeneratingTests, setIsGeneratingTests] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);

    // State variables for DB Viewer Modal
    const [isDbEditMode, setIsDbEditMode] = useState(false);
    const [selectedDbRows, setSelectedDbRows] = useState<number[]>([]);

    // LEGAL POPUP STATE VARIABLES - MOVED INSIDE COMPONENT
    const [isLegalPopupOpen, setIsLegalPopupOpen] = useState(false);
    const [legalContent, setLegalContent] = useState({ title: '', content: '' });
    const [isLoadingLegal, setIsLoadingLegal] = useState(false);

    // Enhanced agent management state
    const [agents, setAgents] = useState<AgentProfile[]>([]);

    const [selectedAgent, setSelectedAgent] = useState<string>('');


    // Navigation state
    const [activeView, setActiveView] = useState<string>('design');

    // AI Configuration state
    const [testwerkApiKey, setTestwerkApiKey] = useState('');
    const [agentApiKey, setAgentApiKey] = useState('');
    const [selectedModel, setSelectedModel] = useState<AIModel>('gpt-4');
    const [showApiKeyModal, setShowApiKeyModal] = useState(false);

    // Modal states
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [currentTestDetails, setCurrentTestDetails] = useState<TestCaseDetails | null>(null);
    const [isDataModalOpen, setIsDataModalOpen] = useState(false);
    const [editableData, setEditableData] = useState<TestCaseDetails | null>(null);
    const [isDbViewerModalOpen, setIsDbViewerModalOpen] = useState(false);
    const [dbTableData, setDbTableData] = useState<any[]>([]);
    const [dbTableHeaders, setDbTableHeaders] = useState<string[]>([]);
    const [isExecuteModalOpen, setIsExecuteModalOpen] = useState(false);
    const [selectedTestsForRun, setSelectedTestsForRun] = useState<string[]>([]);

    // DB Viewer functionality state
    const [dbFilter, setDbFilter] = useState('all');
    const [filteredDbData, setFilteredDbData] = useState<any[]>([]);

    // Edit mode states for View Details modal
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedTestDetails, setEditedTestDetails] = useState<TestCaseDetails | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [openDbViewerOnClose, setOpenDbViewerOnClose] = useState(false);

    // Zoom modal states
    //const [isLogsZoomed, setIsLogsZoomed] = useState(false);
    //const [isResultsZoomed, setIsResultsZoomed] = useState(false);

    // Add state for mode selection (in your App component)
    const [testingMode, setTestingMode] = useState('Agent Test Mode');
    const [useNewCommandCenter, setUseNewCommandCenter] = useState(true); // NEW: Toggle for new UI

    // ADD these state variables with your existing ones
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUser, setCurrentUser] = useState<{ name: string, type: string } | null>(null);
    const [showDashboard, setShowDashboard] = useState(false);

    // Environment Popup State - RAMANA Added 03.08.2025
    const [isEnvironmentPopupOpen, setIsEnvironmentPopupOpen] = useState(false);


    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const [isAdminPopupOpen, setIsAdminPopupOpen] = useState(false);
    const [isAnalyticsDashboardOpen, setIsAnalyticsDashboardOpen] = useState(false);

    const [showGeneratorModal, setShowGeneratorModal] = useState(false);

    const [showAIConfirmation, setShowAIConfirmation] = useState(false);
    const [showAIConfig, setShowAIConfig] = useState(false);
    const [aiMode, setAiMode] = useState<'cloud' | 'local'>('cloud');
    const [availableProviders, setAvailableProviders] = useState<any>({});

    const [pendingAIStatus, setPendingAIStatus] = useState(null);

    const [wsConnected, setWsConnected] = useState(false);
    const [defects, setDefects] = useState<any[]>([]);
    const [lastDataRefresh, setLastDataRefresh] = useState<number>(Date.now());

    const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);

    const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

    const [currentScope, setCurrentScope] = useState<'agent' | 'api' | 'application'>('agent');
    const [selectedAPI, setSelectedAPI] = useState<string>('');
    const [selectedApplication, setSelectedApplication] = useState<string>('');

    const [apis, setApis] = useState<any[]>([]);
    const [applications, setApplications] = useState<any[]>([]);

    const [isResourcesPopupOpen, setIsResourcesPopupOpen] = useState(false);



    // Add with other state variables
    const [currentTime, setCurrentTime] = useState(new Date());

    // Add this useEffect for live time updates
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);


    // Fetch available AI providers from config
    useEffect(() => {
        const fetchProviders = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/ai-providers`);
                setAvailableProviders(response.data.providers || {});

                // Check if local Mistral is available
                const localProvider = response.data.providers['mistral-local'];
                if (localProvider) {
                    // You could check health here if needed
                }
            } catch (error) {
                console.error('Failed to fetch AI providers:', error);
            }
        };
        fetchProviders();
    }, []);

    // Enhanced log filtering
    const [logFilters, setLogFilters] = useState({
        ORCHESTRATOR: true,
        TEST_DESIGNER: true,
        AI_VALIDATOR: true,
        SIMULATION_ENV: true,
        SANDBOX: true,
        AGENT_MANAGER: true,
        USER_MANAGEMENT: true,
        DOCUMENT_INTELLIGENCE: true
    });


    const handleHomeClick = () => {
        window.open('https://testwerk.ai', '_blank', 'noopener,noreferrer');
    };

    const handleLegalClick = async (type: 'privacy' | 'imprint' | 'contact') => {
        setIsLoadingLegal(true);
        setIsLegalPopupOpen(true);

        try {
            // Use local backend instead of live site
            const response = await fetch(`${API_BASE_URL}/${type}`, {
                method: 'GET',
                headers: {
                    'Accept': 'text/html,application/xhtml+xml',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const title = doc.querySelector('title')?.textContent || `${type.charAt(0).toUpperCase() + type.slice(1)}`;
            const boxElement = doc.querySelector('.box');

            if (boxElement) {
                // Just use the HTML content - let CSS handle the styling
                const content = `
                <div class="container">
                    <div class="box">${boxElement.innerHTML}</div>
                </div>
            `;
                setLegalContent({ title, content });
                //console.log('Successfully fetched content from local backend');
            } else {
                throw new Error('Box element not found');
            }

        } catch (error) {
            //console.log('Local fetch failed, using fallback content:', error);

            // Simple fallback without inline styles
            const fallbackContent = {
                privacy: {
                    title: 'Privacy Policy',
                    content: '<div class="container"><div class="box"><h1>Privacy Policy</h1><p>Privacy policy content...</p></div></div>'
                },
                imprint: {
                    title: 'Imprint',
                    content: '<div class="container"><div class="box"><h1>Imprint</h1><p>Imprint content...</p></div></div>'
                },
                contact: {
                    title: 'Contact',
                    content: '<div class="container"><div class="box"><h1>Contact</h1><p>Contact information...</p></div></div>'
                }
            };

            setLegalContent(fallbackContent[type]);
        } finally {
            setIsLoadingLegal(false);
        }
    };


    // LEGAL HANDLER FUNCTION - MOVED INSIDE COMPONENT
    const handleLegalClickOld = async (type: 'privacy' | 'imprint' | 'contact') => {
        setIsLoadingLegal(true);
        setIsLegalPopupOpen(true);

        try {
            // Use local backend instead of live site
            const response = await fetch(`${API_BASE_URL}/${type}`, {
                method: 'GET',
                headers: {
                    'Accept': 'text/html,application/xhtml+xml',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const title = doc.querySelector('h1')?.textContent || `${type.charAt(0).toUpperCase() + type.slice(1)}`;
            const boxElement = doc.querySelector('.box');

            if (boxElement) {
                const content = `<div class="box">${boxElement.innerHTML}</div>`;
                setLegalContent({ title, content });
                //console.log('Successfully fetched styled content from local backend');
            } else {
                throw new Error('Box element not found');
            }

        } catch (error) {
            //console.log('Local fetch failed, using fallback content:', error);

            // Fallback content instead of redirecting
            const fallbackContent = {
                privacy: { title: 'Privacy Policy', content: '<div class="box"><p>Privacy policy content...</p></div>' },
                imprint: { title: 'Imprint', content: '<div class="box"><p>Imprint content...</p></div>' },
                contact: { title: 'Contact', content: '<div class="box"><p>Contact information...</p></div>' }
            };

            setLegalContent(fallbackContent[type]);
        } finally {
            setIsLoadingLegal(false);
        }
    };


    // DB Viewer filtering logic
    useEffect(() => {
        const applyFilter = (data: any[], filter: string) => {
            if (!data) return [];
            if (filter === 'all') return data;
            if (['Functional', 'Reliability', 'Guardrail', 'Compliance'].includes(filter)) {
                return data.filter(row => row.type === filter);
            }
            // Assumes filter is an agent name
            return data.filter(row => row.agent_name === filter);
        };
        const filtered = applyFilter(dbTableData, dbFilter);
        setFilteredDbData(filtered);
    }, [dbTableData, dbFilter]);

    // Handle system actions from Settings drawer
    useEffect(() => {
        const handleSystemAction = (event: any) => {
            const action = event.detail;

            // Close settings drawer first
            //setIsSettingsDrawerOpen(false);

            // Then open the appropriate modal/view
            switch (action) {
                case 'environment':
                    setIsEnvironmentPopupOpen(true);
                    break;
                case 'logs':
                    // Handle logs view -  might need to add a logs modal state
                    setIsLogsModalOpen(true);
                    break;
                case 'admin':
                    setIsAdminPopupOpen(true);
                    break;
                case 'resources':
                    setIsResourcesPopupOpen(true);
                    break;
            }
        };

        window.addEventListener('system-action', handleSystemAction);
        return () => window.removeEventListener('system-action', handleSystemAction);
    }, []);

    // DB Viewer handler functions
    const handleSelectDbRow = (rowIndex: number, isChecked: boolean) => {
        if (isChecked) {
            setSelectedDbRows(prev => [...prev, rowIndex]);
        } else {
            setSelectedDbRows(prev => prev.filter(i => i !== rowIndex));
        }
    };

    const handleSelectAllDbRows = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedDbRows(filteredDbData.map((_, index) => index));
        } else {
            setSelectedDbRows([]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedDbRows.length === 0) return;
        //if (window.confirm(`Are you sure you want to delete ${selectedDbRows.length} selected test cases? This is a mock action.`)) {
        const selectedIds = selectedDbRows.map(index => filteredDbData[index].id);
        const remainingData = dbTableData.filter(row => !selectedIds.includes(row.id));
        setDbTableData(remainingData);
        setSelectedDbRows([]);
        //alert(`${selectedDbRows.length} test cases deleted.`);
        // }
    };

    const handleBulkDuplicate = async () => {
        if (selectedDbRows.length === 0) return;
        const duplicatedRows = selectedDbRows.map(index => {
            const originalRow = filteredDbData[index];
            return {
                ...originalRow,
                name: `${originalRow.name}_copy_${Date.now()}`,
                id: `${originalRow.id}_copy_${Date.now()}`
            };
        });
        setDbTableData(prev => [...prev, ...duplicatedRows]);
        setSelectedDbRows([]);
        alert(`${selectedDbRows.length} test cases duplicated (mock).`);
    };

    const handleEditSelectedDbRow = () => {
        if (selectedDbRows.length !== 1) return alert("Please select exactly one row to edit.");
        const testCaseToEdit = filteredDbData[selectedDbRows[0]];
        if (testCaseToEdit) {
            setCurrentTestDetails(testCaseToEdit as TestCaseDetails);
            setOpenDbViewerOnClose(true);
            setIsDbViewerModalOpen(false);
            setIsDetailsModalOpen(true);
        }
    };

    const handleDetailsModalClose = () => {
        setIsDetailsModalOpen(false);
        setCurrentTestDetails(null);
        if (openDbViewerOnClose) {
            setIsDbViewerModalOpen(true);
            setOpenDbViewerOnClose(false);
        }
    };

    // DB Field Edit Handler
    const handleDbFieldEdit = async (rowIndex: number, field: string, newValue: any) => {
        const originalRow = filteredDbData[rowIndex];
        if (!originalRow) return;

        try {
            const updatedRow = { ...originalRow, [field]: newValue };

            const masterIndex = dbTableData.findIndex(item => item.id === originalRow.id);
            const newMasterData = [...dbTableData];

            if (masterIndex > -1) {
                newMasterData[masterIndex] = updatedRow;
                setDbTableData(newMasterData);
            }

            await axios.put(`${API_BASE_URL}/db/test-cases/${originalRow.name || originalRow.id}`, {
                field: field,
                value: newValue,
                agent_name: originalRow.agent_name
            });

            setLogs(prev => [...prev, ` AI: Smart edit applied to ${field}`]);

        } catch (error) {
            console.error("DB edit failed:", error);
            alert("Failed to save changes. Please try again.");
            const response = await axios.get(`${API_BASE_URL}/db/test-cases`);
            setDbTableData(response.data);
        }
    };

    // Agent management functions
    const addAgent = (agentData: Omit<AgentProfile, 'id' | 'testCaseCount'>) => {
        const newAgent: AgentProfile = { ...agentData, id: Date.now().toString(), testCaseCount: 0 };
        setAgents(prev => [...prev, newAgent]);
    };

    const updateAgent = (agentId: string, updates: Partial<AgentProfile>) => {
        setAgents(prev => prev.map(agent => (agent.id === agentId ? { ...agent, ...updates } : agent)));
    };

    const deleteAgent = (agentId: string) => {
        setAgents(prev => prev.filter(agent => agent.id !== agentId));
        if (selectedAgent === agents.find(a => a.id === agentId)?.name) {
            setSelectedAgent(agents[0]?.name || '');
        }
    };


    const fetchTestCases = async (agentName?: string) => {
        const targetAgent = agentName || selectedAgent;
        if (!targetAgent) return; // Don't fetch if no agent is selected

        try {
            // First, get the test case names for the active agent (for the Execute Modal)
            const response = await axios.get(`${API_BASE_URL}/test-cases/${targetAgent}`);
            const testNames = response.data || [];

            //console.log(`[TestCases] Fetched ${testNames.length} test cases for agent: ${targetAgent}`);
            //console.log(`[TestCases] Test names:`, testNames);

            setTestCases(testNames);

            // Second, and most importantly, refresh the entire agent list
            // This will update the testCaseCount for the target agent and re-render the UI
            await fetchIntelligentAgents();

            if (testNames.length > 0 && !testNames.includes(selectedTest)) {
                setSelectedTest(testNames[0]);
            } else if (testNames.length === 0) {
                setSelectedTest('');
            }
        } catch (error) {
            console.error(`Failed to fetch test cases for ${targetAgent}:`, error);
            setTestCases([]);
            // Still try to refresh the agent list even if fetching test names fails
            await fetchIntelligentAgents();
        }
    };

    const fetchDefects = async (agentName: string) => {
        if (!agentName) {
            setDefects([]);
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/db/defects?agent_name=${agentName}`);
            const data = await response.json();
            setDefects(data);
            //console.log('[App] Fetched defects:', data.length);
        } catch (error) {
            console.error('Failed to fetch defects:', error);
            setDefects([]);
        }
    };

    const loadAgentTestResults = async (agentName: string) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/db/test-executions`);
            const allExecutions = response.data.filter(
                (exec: any) => exec.agent_name === agentName
            );

            // 🔍 DEBUG: Log what we actually get
            //console.log('🔍 First execution record:', allExecutions[0]);
            //console.log('🔍 Available fields:', Object.keys(allExecutions[0] || {}));
            //console.log('🔍 AI Analysis fields:', {
            //    validation_analysis: allExecutions[0]?.validation_analysis,
            //    failure_reason: allExecutions[0]?.failure_reason,
            //    ai_reasoning: allExecutions[0]?.ai_reasoning
            //});

            const agentResults = new Map<string, TestResult>();
            allExecutions.forEach((exec: any) => {
                agentResults.set(exec.test_name, {
                    test_name: exec.test_name,
                    status: exec.status,
                    output: exec.agent_output || 'No output recorded',
                    ai_confidence: exec.ai_confidence || 0,
                    validation_analysis: exec.validation_analysis || exec.failure_reason || 'Analysis not available',
                    task_prompt: exec.task_prompt || '',
                    expected_outcome: exec.expected_outcome || '',
                    mock_data: exec.mock_data || {}
                });
            });

            setResults(agentResults);
        } catch (error) {
            console.error('Failed to load agent test results:', error);
            setResults(new Map());
        }
    };

    const fetchAIConfig = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/ai-config`);
            setAiConfig(response.data);
            //console.log('[Debug] AI Config fetched:', response.data);
            setSelectedModel(response.data.default_model);
        } catch (error) {
            console.error("Failed to fetch AI config:", error);
        }
    };

    const fetchEntities = async () => {
        try {
            // First, fetch the intelligent agents with all their details
            const intelligentAgents = await fetchIntelligentAgents();

            // Then fetch APIs and Applications in parallel
            const [apisRes, appsRes, countsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/endpoints`),
                fetch(`${API_BASE_URL}/db/applications`),
                fetch(`${API_BASE_URL}/api/entity-test-counts`)
            ]);

            const apisData = await apisRes.json();
            const appsData = await appsRes.json();
            const countsData = await countsRes.json();

            //console.log('API Response:', apisData);  // ADDED THIS for debugging
            //console.log('Apps Response:', appsData);  // ADDED THIS for debugging
            //console.log('Test counts data:', countsData);  // Debug log


            // Update APIs state
            //if (apisData.success && apisData.endpoints) {
            //    setApis(apisData.endpoints || []);
            //}

            // Update APIs with test counts
            if (apisData.success && apisData.endpoints) {
                const apisWithCounts = apisData.endpoints.map((api: any) => ({
                    ...api,
                    testCount: countsData.counts?.api?.[api.name] || 0
                }));
                setApis(apisWithCounts);
            }

            // Update Applications state
            if (Array.isArray(appsData)) {
                const appsWithCounts = appsData.map((app: any) => ({
                    ...app,
                    testCount: countsData.counts?.application?.[app.name] || 0  // Add test count
                }));
                setApplications(appsWithCounts);
            }
            /*
            console.log('Fetched all entities:', {
                agents: intelligentAgents.length,
                apis: apisData.endpoints?.length || 0,
                applications: appsData?.length || 0
            });
            */
            return {
                agents: intelligentAgents,
                apis: apisData.endpoints || [],
                applications: appsData || []
            };
        } catch (error) {
            console.error('Failed to fetch entities:', error);
            return {
                agents: agents,  // Return current state on error
                apis: apis,
                applications: applications
            };
        }
    };

    const fetchIntelligentAgents = async () => {
        try {
            const agentResponse = await axios.get(`${API_BASE_URL}/agent-manager/agents/intelligent`);
            const intelligentAgents = agentResponse.data.agents || [];

            // Convert agents and fetch their test case counts in parallel
            const convertedAgents: AgentProfile[] = await Promise.all(
                intelligentAgents.map(async (agent: any) => {
                    let testCaseCount = 0;
                    try {
                        const testResponse = await axios.get(`${API_BASE_URL}/test-cases/${agent.name}`);
                        testCaseCount = (testResponse.data || []).length;
                    } catch (error) {
                        // This is not a critical error, just means no tests found for that agent
                        // console.warn(`Could not fetch test cases for ${agent.name}:`, error);
                    }

                    // This now matches the EnhancedAgentProfile structure
                    return {
                        id: agent.agent_id || `intelligent-${agent.name}`,
                        name: agent.name,
                        description: agent.enhanced_mode
                            ? `Enhanced ${agent.domain || 'General'} Agent`
                            : `Standard Agent for ${agent.domain}`,
                        type: agent.type,
                        domain: agent.domain,
                        isActive: agent.status === 'ready',
                        testCaseCount: testCaseCount,
                        // Add other enhanced fields directly
                        enhanced_mode: agent.enhanced_mode,
                        custom_documents_count: agent.custom_documents_count,
                        has_user_instructions: agent.has_user_instructions,
                        suggested_domain: agent.suggested_domain,
                        confidence_score: agent.confidence_score,
                        capabilities_count: agent.capabilities_count,
                        created_at: agent.created_at,
                    };
                })
            );

            // De-duplicate agents to be safe
            const uniqueAgents = convertedAgents.reduce((acc: AgentProfile[], current) => {
                if (!acc.find(agent => agent.name === current.name)) {
                    acc.push(current);
                }
                return acc;
            }, []);

            setAgents(uniqueAgents);

            // Auto-select the first agent if none is selected
            /* RAMANA COMMENTED to show Select Agent ... msg. 
            if (uniqueAgents.length > 0 && !selectedAgent) {
                setSelectedAgent(uniqueAgents[0].name);
            }
            */
            return uniqueAgents; // Return the fresh list

        } catch (error) {
            console.warn('⚠️ Network error fetching agents:', error);
            // On failure, return the current list to avoid clearing the UI on a temp network issue
            return agents;
        }
    };


    const fetchIntelligentAgentsNewWorking = async () => {
        try {
            //console.log('Fetching intelligent agents...');

            //const response = await fetch(`${API_BASE_URL}/agent-manager/agents/intelligent`);
            const response = await fetch(`${API_BASE_URL}/agent-manager/agents`);


            if (!response.ok) {
                console.warn(`⚠️ Agent fetch failed: ${response.status} - Service may be processing`);

                // Don't clear existing agents if service is temporarily unavailable
                if (agents.length > 0) {
                    //console.log('Keeping existing agents during service restart');
                    return; // Keep current agents
                }

                // Only clear if we have no agents and it's a real error
                setAgents([]);
                return;
            }

            const data = await response.json();
            const intelligentAgents = data.agents || [];
            //console.log(`Found ${intelligentAgents.length} intelligent agents`);

            // Convert intelligent agents to AgentProfile format with test counts
            const convertedAgents: AgentProfile[] = await Promise.all(
                intelligentAgents.map(async (agent: any) => {
                    let testCaseCount = 0;
                    try {
                        const testResponse = await axios.get(`${API_BASE_URL}/test-cases/${agent.name}`);
                        testCaseCount = (testResponse.data || []).length;
                    } catch (error) {
                        console.warn(`Could not fetch test cases for ${agent.name}:`, error);
                    }

                    return {
                        id: agent.agent_id || `intelligent-${agent.name}`,
                        name: agent.name,
                        description: `Intelligent ${agent.type} agent for ${agent.domain}`,
                        type: agent.type,
                        domain: agent.domain,
                        isActive: agent.status === 'ready',
                        testCaseCount: testCaseCount
                    };
                })
            );

            // ENHANCED: Better duplicate detection by both name AND agent_id
            const uniqueAgents = convertedAgents.reduce((acc: AgentProfile[], current) => {
                // Check if agent already exists by name or id
                const existingAgent = acc.find(agent =>
                    agent.name === current.name || agent.id === current.id
                );

                if (!existingAgent) {
                    acc.push(current);
                } else {
                    //console.warn(`Skipping duplicate agent: ${current.name} (ID: ${current.id})`);
                }

                return acc;
            }, []);

            //console.log(`Processed ${intelligentAgents.length} raw → ${convertedAgents.length} converted → ${uniqueAgents.length} unique agents`);

            setAgents(uniqueAgents);

            if (uniqueAgents.length > 0 && !selectedAgent) {
                setSelectedAgent(uniqueAgents[0].name);
            }

            //console.log(`Successfully loaded ${uniqueAgents.length} unique agents`);

        } catch (error) {
            console.warn('⚠️ Network error fetching agents:', error);
            // Don't clear existing agents on network errors
        }
    };

    const saveTestCaseChanges = async () => {
        if (!editedTestDetails || !currentTestDetails) return;
        setIsSaving(true);
        try {
            await axios.put(`${API_BASE_URL}/test-case/${selectedAgent}/${currentTestDetails.name}`, {
                task_prompt: editedTestDetails.task_prompt,
                expected_outcome: editedTestDetails.expected_outcome,
                mock_data: editedTestDetails.mock_data,
                simulation_config: editedTestDetails.simulation_config,
                type: editedTestDetails.type
            });
            setCurrentTestDetails(editedTestDetails);
            setIsEditMode(false);
            alert("Test case updated successfully!");
            await fetchTestCases();
            await fetchDefects(selectedAgent);
        } catch (error) {
            console.error("Error saving test case:", error);
            alert("Failed to save test case changes. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    // Split into two useEffects
    useEffect(() => {

        // Only fetch if authenticated
        //if (!isAuthenticated) return;
        // Clear previous agent's selected tests
        setSelectedTestsForRun([]);

        // Clear previous agent's test results  
        //setResults(new Map());


        // Only fetch test cases if we have a selected agent
        if (selectedAgent && selectedAgent.trim() !== '') {
            fetchTestCases();
            fetchDefects(selectedAgent);
            loadAgentTestResults(selectedAgent);
        }
    }, [selectedAgent]); // This only runs when selectedAgent changes

    useEffect(() => {

        // Only run after authentication
        //if (!isAuthenticated) return;
        // Run once on component mount
        fetchAIConfig();
        //fetchIntelligentAgents();
        fetchEntities();

        const fetchHealth = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/health`);
                setStatus(response.data);
            } catch (error) {
                console.error("Failed to fetch health:", error);
            }
        };

        fetchHealth();
        const healthInterval = setInterval(fetchHealth, 10000);

        // WebSocket - only connect once
        let ws: WebSocket | null = null;

        try {
            if (WS_URL && WS_URL !== 'undefined' && WS_URL !== 'ws://undefined' && !WS_URL.includes('undefined')) {
                ws = new WebSocket(WS_URL);

                ws.onopen = () => {
                    //console.log('WebSocket connected for live logs');
                    setWsConnected(true);
                };

                ws.onmessage = (event) => {
                    setLogs(prev => [...prev.slice(-99), event.data]);
                };

                ws.onerror = () => {
                    console.warn('⚠️ WebSocket connection failed - logs will not update live');
                };

                ws.onclose = () => {
                    //console.log('WebSocket connection closed');
                    setWsConnected(false);
                    // Attempt reconnection after 2 seconds
                    setTimeout(() => {
                        if (WS_URL && WS_URL !== 'undefined') {
                            const newWs = new WebSocket(WS_URL);
                            // Copy all the event handlers here
                            newWs.onopen = () => {
                                //console.log('WebSocket reconnected');
                                setWsConnected(true);
                            };
                            newWs.onmessage = (event) => {
                                setLogs(prev => [...prev.slice(-99), event.data]);
                            };
                            newWs.onclose = () => {
                                //console.log('WebSocket connection closed again');
                                setWsConnected(false);
                            };
                        }
                    }, 2000);
                };
            }
        } catch (error) {
            console.warn('⚠️ WebSocket unavailable:', error);
        }

        return () => {
            clearInterval(healthInterval);
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.close();
            }
        };
    }, []); // Empty dependency array - runs only once on mount

    // Add this useEffect to refresh agents when switching tabs
    //useEffect(() => {
    //if (activeView === 'design' || activeView === 'execution') {
    //        fetchIntelligentAgents();
    //    }
    //}, [activeView]);

    /* RAMANA commented out to make sure Select Agent... is shown not first agent always in dropdown
    useEffect(() => {
        if (agents.length > 0 && !selectedAgent) {
            setSelectedAgent(agents[0].name); // Auto-select first available agent
        } else if (agents.length === 0) {
            setSelectedAgent(''); // Clear selection when no agents
        }
    }, [agents, selectedAgent]);
    */
    const handleUpdateAIConfig = async () => {
        try {
            await axios.post(`${API_BASE_URL}/update-ai-config`, {
                testwerk_api_key: testwerkApiKey || undefined,
                agent_api_key: agentApiKey || undefined,
                default_model: selectedModel
            });
            TestWerkToasts.aiConfigUpdated(selectedModel);
            setShowApiKeyModal(false);
            fetchAIConfig();
        } catch (error) {
            alert("Error updating AI configuration.");
        }
    };

    const handleSaveChanges = async () => {
        if (!editableData) return;
        try {
            await axios.post(`${API_BASE_URL}/update-test-case`, { agent_name: selectedAgent, test_case: editableData });
            alert("Test data saved successfully!");
            setIsDataModalOpen(false);
        } catch (error) {
            alert("Error saving test data.");
        }
    };

    const handleSelectAllTests = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedTestsForRun(e.target.checked ? testCases : []);
    };

    const handleSingleTestSelect = (testName: string, isChecked: boolean) => {
        if (isChecked) {
            setSelectedTestsForRun(prev => [...prev, testName]);
        } else {
            setSelectedTestsForRun(prev => prev.filter(name => name !== testName));
        }
    };

    const handleRunSelectedTests = async () => {
        if (selectedTestsForRun.length === 0) {
            alert("Please select at least one test case to run.");
            return;
        }
        if (!aiConfig?.testwerk_api_key_configured) {
            if (window.confirm("AI services are not configured. Would you like to set up API keys now?")) {
                setShowApiKeyModal(true);
            }
            return;
        }
        setIsTestRunning(true);
        setIsExecuteModalOpen(false);
        setLogs(prev => [...prev, `\n--- UI: Starting AI-driven test suite (${selectedTestsForRun.length} tests) ---\n`]);
        setResults(prev => {
            const newResults = new Map(prev);
            selectedTestsForRun.forEach(name => {
                newResults.set(name, { test_name: name, status: 'RUNNING', output: 'AI validation in progress...' });
            });
            return newResults;
        });
        try {
            const response = await axios.post(`${API_BASE_URL}/run-test-suite`, {
                agent_name: selectedAgent,
                test_names: selectedTestsForRun
            });
            setResults(prev => {
                const newResults = new Map(prev);
                response.data.forEach((result: TestResult) => {
                    newResults.set(result.test_name, result);
                });
                return newResults;
            });
        } catch (error) {
            alert(`Failed to run test suite: ${axios.isAxiosError(error) ? error.message : String(error)}`);
        } finally {
            setIsTestRunning(false);
            // Wait a moment for defects to be saved
            setTimeout(() => {
                fetchDefects(selectedAgent);
                //console.log('[App] Refreshing defects after test execution for:', agentName);
                // Trigger dashboard refresh for token updates
                setLastDataRefresh(Date.now());
            }, 1000);
        }
    };



    const filteredLogs = logs.filter(log => {
        const serviceName = Object.keys(logFilters).find(service => log.includes(`[${service}]`));
        return !serviceName || logFilters[serviceName as keyof typeof logFilters];
    });


    // ADD this function after your existing handlers
    //const handleLogin = (userData: { name: string, type: 'token' | 'userpass' }, aiStatus?: any) => {
    //    setCurrentUser(userData);

    //    if (aiStatus?.configured) {
    // Show AI confirmation dialog
    //        setPendingAIStatus(aiStatus);
    //        setShowAIConfirmation(true);
    //    } else {
    // Force AI configuration
    //        setShowAIConfig(true);
    //    }

    // don't set authenticated/dashboard immediately:
    // setIsAuthenticated(true);
    // setShowDashboard(true);
    //};

    // RAMANA ADDED to go to MAIN SCREEN AFTER LOGIN - NO AI CONFIG
    const handleLogin = (userData: { name: string, type: 'token' | 'userpass' }, aiStatus?: any) => {
        setCurrentUser(userData);

        // Skip all AI config popups - go directly to main UI
        setIsAuthenticated(true);
        setShowDashboard(false);  // false = main UI, true = dashboard

        // Store AI status if needed for later reference
        if (aiStatus) {
            setPendingAIStatus(aiStatus);
        }
    };


    const handleLogout = () => {
        setCurrentUser(null);
        setIsAuthenticated(false);
        setShowDashboard(false);
        setActiveView('design'); // Reset to default view
    };

    // 3. UPDATE the Dashboard button click handler (around line 300):
    const handleDashboardModeSelect = (mode: string) => {
        // Instead of setting activeView, show Analytics Dashboard
        setIsAnalyticsDashboardOpen(true);

        // Keep the existing mode mapping for other potential uses
        const modeMap = {
            'design': 'Agent Test',
            'execution': 'System Test',
            'agents': 'Agent Management',
            'config': 'Configuration',
            'analytics': 'Analytics',
            'terminal': 'AI Terminal'
        };

        setTestingMode(modeMap[mode as keyof typeof modeMap] || 'Agent Test Mode');
    };

    return (
        <Router>
            <div className="App">
                {/* ALWAYS SHOW HEADER - Content changes based on state */}
                <header className="main-header">
                    <div className="logo-group">
                        <span className="logo-tw">TW</span>
                        <span className="logo-subtext">testwerk.ai</span>
                    </div>

                    {/* CONDITIONAL HEADER CENTER - Only show navigation in main views 
                    {isAuthenticated && !showDashboard && (
                        <div className="header-center">
                            <NavigationButtons activeView={activeView} setActiveView={setActiveView} />
                        </div>
                    )}
                        */}


                    {/* HEADER CENTER - Time display only */}
                    {isAuthenticated && !showDashboard && (
                        <div className="header-center" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            {/* Date and time display */}
                            <div style={{
                                color: '#01cc01ff',
                                fontSize: '0.85rem',
                                fontFamily: 'monospace'
                            }}>
                                {currentTime.toLocaleString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false
                                }).replace(',', '')}
                            </div>
                        </div>
                    )}

                    {/* CONDITIONAL HEADER RIGHT - Different content based on state */}
                    <div className="header-right">
                        {!isAuthenticated ? (
                            // LOGIN STATE: Empty or minimal info
                            <div className="user-info">
                                <span style={{ color: '#999', fontSize: '0.9rem' }}>Please login</span>
                            </div>

                        ) : (
                            // MAIN VIEWS STATE: Full status + navigation
                            <>
                                {/* Activity Status Indicator - NEW */}
                                <button
                                    className="pushable push-primary btn-sm"
                                    style={{ cursor: 'default' }}
                                >
                                    <span className="shadow"></span>
                                    <span className="edge"></span>
                                    <span className="front" style={{
                                        color: isTestRunning ? '#ffc107' :
                                            isGeneratingTests ? '#8b5cf6' :
                                                (status && Object.values(status).filter(v => v === 'ok' || v === true).length >= 8) ? '#28a745' : '#ff6b6b'
                                    }}>
                                        {isTestRunning ? 'Running Tests...' :
                                            isGeneratingTests ? 'Generating...' :
                                                (status && Object.values(status).filter(v => v === 'ok' || v === true).length >= 8) ?
                                                    (() => {
                                                        if (currentScope === 'agent' && selectedAgent) return 'Agent Ready';
                                                        if (currentScope === 'api' && selectedAPI) return 'API Ready';
                                                        if (currentScope === 'application' && selectedApplication) return 'App Ready';
                                                        if (currentScope === 'agent' && !selectedAgent) return 'Select Agent';
                                                        if (currentScope === 'api' && !selectedAPI) return 'Select API';
                                                        if (currentScope === 'application' && !selectedApplication) return 'Select App';
                                                        return 'System Ready';
                                                    })() :
                                                    'System Starting...'}
                                    </span>
                                </button>

                                <div className="status-group">
                                    {/* Enhanced AI Status Button */}
                                    {/*
                                    <button
                                        className={`pushable ${aiConfig?.testwerk_api_key_configured ? 'push-primary' : 'push-danger'} btn-sm`}
                                        onClick={() => setShowApiKeyModal(true)}
                                        title="AI Configuration Status - Click to configure"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">
                                            {(() => {
                                                // Check if AI is configured
                                                if (!aiConfig?.testwerk_api_key_configured) {
                                                    return 'NONE';
                                                }

                                                const model = aiConfig?.default_model || '';

                                                // Local mode
                                                if (aiMode === 'local') {
                                                    if (model.includes('mistral')) {
                                                        return 'Mistral: Local';
                                                    } else if (model.includes('llama')) {
                                                        return 'Llama: Local';
                                                    } else {
                                                        return 'Local AI';
                                                    }
                                                }

                                                // Cloud mode - show provider/model name
                                                if (model.includes('gpt-4')) {
                                                    return 'GPT-4';
                                                } else if (model.includes('gpt-3')) {
                                                    return 'GPT-3.5';
                                                } else if (model.includes('claude')) {
                                                    return 'Claude';
                                                } else if (model.includes('gemini')) {
                                                    return 'Gemini';
                                                } else if (model.includes('mistral') && !model.includes(':')) {
                                                    return 'Mistral Cloud';
                                                } else {
                                                    // Fallback - show first part of model name
                                                    const modelName = model.split('-')[0].toUpperCase();
                                                    return modelName || 'AI Ready';
                                                }
                                            })()}
                                        </span>
                                    </button> */}
                                    {/*
                                    <div className="testing-mode-indicator">
                                        <span className="mode-badge">{testingMode.toUpperCase()}</span>
                                    </div>
                                    <div className="user-info-indicator">
                                        <span className="user-badge">{currentUser?.name || 'User'}</span>
                                    </div>
                                    */}
                                </div>


                                <div className="user-actions">
                                    {/*
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => setIsEnvironmentPopupOpen(true)}
                                        title="View Environment Status"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Environment</span>
                                    </button>
                                 */}
                                    {/*
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => setIsAnalyticsDashboardOpen(true)}
                                        title="Open Analytics Dashboard"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Dashboard</span>
                                    </button>
                                */}
                                    {/*
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => setShowGeneratorModal(true)}
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Generator</span>
                                    </button>
                                

                                    <button
                                        className="pushable push-warning btn-sm"
                                        onClick={() => setResults(new Map())}
                                        title="Clear all test results (temporary)"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Clear Results</span>
                                    </button>
                                    

                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => setIsAdminPopupOpen(true)}
                                        title="Open Admin Console"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">User Admin</span>
                                    </button>
                                    */}

                                    {/* AI Mode Toggle */}
                                    <div className="ai-mode-toggle" style={{ marginRight: '10px', display: 'inline-flex', alignItems: 'center' }}>
                                        <button
                                            className={`pushable ${aiMode === 'cloud' ? 'push-primary' : 'push-secondary'} btn-sm`}
                                            onClick={async () => {
                                                const newMode = aiMode === 'cloud' ? 'local' : 'cloud';
                                                setAiMode(newMode);

                                                try {
                                                    // Update backend about mode change
                                                    await axios.post(`${API_BASE_URL}/update-ai-mode`, { mode: newMode });

                                                    // Re-fetch AI config to get updated provider status
                                                    await fetchAIConfig();

                                                    // After fetching config, set model based on what's actually configured
                                                    if (newMode === 'cloud') {
                                                        // Check which cloud provider has configured keys
                                                        const configResponse = await axios.get(`${API_BASE_URL}/ai-config`);
                                                        const config = configResponse.data;

                                                        // Priority order: OpenAI -> Claude -> Gemini -> Mistral Cloud
                                                        if (config.openai_api_key) {
                                                            setSelectedModel('gpt-4');
                                                        } else if (config.anthropic_api_key) {
                                                            setSelectedModel('claude-3-sonnet-20240229');
                                                        } else if (config.google_api_key || config.gemini_api_key) {
                                                            setSelectedModel('gemini-pro');
                                                        } else if (config.mistral_api_key) {
                                                            setSelectedModel('mistral-large-latest');
                                                        } else {
                                                            // No keys configured, default to GPT-4 but will show as unconfigured
                                                            setSelectedModel('gpt-4');
                                                        }
                                                    } else {
                                                        // Local mode - always use local mistral
                                                        setSelectedModel('mistral:7b');
                                                    }
                                                } catch (error) {
                                                    console.error('Failed to switch AI mode:', error);
                                                }
                                            }}
                                            title={`Switch to ${aiMode === 'cloud' ? 'Local' : 'Cloud'} AI`}
                                        >
                                            <span className="shadow"></span>
                                            <span className="edge"></span>
                                            <span className="front">
                                                {aiMode === 'cloud' ? 'AI :: Cloud' : 'AI :: Local'}
                                            </span>
                                        </button>
                                    </div>

                                    {/* Settings Button */}
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => setIsSettingsDrawerOpen(true)}
                                        title="Platform Settings"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Settings</span>
                                    </button>

                                    {/*
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => setIsAdminPopupOpen(true)}
                                        title="Open Admin Console"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Admin</span>
                                    </button>

                                    <button
                                        className="pushable push-primary btn-sm"
                                        style={{ cursor: 'default' }}
                                        title="Current User"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">{currentUser?.name || 'User'}</span>
                                    </button>
                                    */}
                                    <button
                                        className="pushable push-secondary btn-sm"
                                        onClick={handleLogout}
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Logout</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </header>

                {/* MAIN CONTENT - Always wrapped in main-content */}
                <main className="main-content">
                    {!isAuthenticated ? (
                        <>
                            <LoginScreen onLogin={handleLogin} />

                            {showAIConfirmation && (
                                <AIConfirmationDialog
                                    aiStatus={pendingAIStatus}
                                    onProceed={() => {
                                        setShowAIConfirmation(false);
                                        setIsAuthenticated(true);
                                        setShowDashboard(false); //changed from true - RAMANA for skip AI Config MODEL
                                    }}
                                    onConfigure={() => {
                                        setShowAIConfirmation(false);
                                        setShowAIConfig(true);
                                    }}
                                />
                            )}

                            {showAIConfig && (
                                <AIConfigModal
                                    isOpen={showAIConfig}
                                    onClose={() => setShowAIConfig(false)}
                                    aiConfig={aiConfig}
                                    testwerkApiKey={testwerkApiKey}
                                    setTestwerkApiKey={setTestwerkApiKey}
                                    agentApiKey={agentApiKey}
                                    setAgentApiKey={setAgentApiKey}
                                    selectedModel={selectedModel}
                                    setSelectedModel={setSelectedModel}
                                    onUpdateConfig={() => {
                                        setShowAIConfig(false);
                                        setIsAuthenticated(true);
                                        setShowDashboard(true);
                                        handleUpdateAIConfig();
                                    }}
                                    aiMode={aiMode}
                                    availableProviders={availableProviders}
                                />
                            )}
                        </>
                    ) : (
                        <DesignStudioViewNew
                            agents={agents}
                            selectedAgent={selectedAgent}
                            setSelectedAgent={setSelectedAgent}
                            testCases={testCases}
                            selectedTest={selectedTest}
                            setSelectedTest={setSelectedTest}
                            isGeneratingTests={isGeneratingTests}
                            setIsGeneratingTests={setIsGeneratingTests}
                            fetchTestCases={fetchTestCases}
                            aiConfig={aiConfig}
                            addAgent={addAgent}
                            updateAgent={updateAgent}
                            deleteAgent={deleteAgent}
                            logs={filteredLogs}
                            setLogs={setLogs}
                            setResults={setResults}
                            setIsDetailsModalOpen={setIsDetailsModalOpen}
                            setCurrentTestDetails={setCurrentTestDetails}
                            setIsDataModalOpen={setIsDataModalOpen}
                            setEditableData={setEditableData}
                            setIsDbViewerModalOpen={setIsDbViewerModalOpen}
                            setDbTableData={setDbTableData}
                            setDbTableHeaders={setDbTableHeaders}
                            showUploadModal={showUploadModal}
                            setShowUploadModal={setShowUploadModal}
                            showTemplateLibrary={showTemplateLibrary}
                            setShowTemplateLibrary={setShowTemplateLibrary}
                            isUploading={isUploading}
                            setIsUploading={setIsUploading}
                            fetchIntelligentAgents={fetchIntelligentAgents}
                            apis={apis}
                            applications={applications}
                            fetchEntities={fetchEntities}
                            results={results}
                            setIsExecuteModalOpen={setIsExecuteModalOpen}
                            isTestRunning={isTestRunning}
                            status={status}
                            logFilters={logFilters}
                            setLogFilters={setLogFilters}
                            testwerkApiKey={testwerkApiKey}
                            setTestwerkApiKey={setTestwerkApiKey}
                            agentApiKey={agentApiKey}
                            setAgentApiKey={setAgentApiKey}
                            selectedModel={selectedModel}
                            setSelectedModel={setSelectedModel}
                            onUpdateAIConfig={handleUpdateAIConfig}
                            fetchTestCases={fetchTestCases}
                            onOpenDashboard={() => setIsAnalyticsDashboardOpen(true)}
                            onOpenEnvironment={() => setIsEnvironmentPopupOpen(true)}
                            loadAgentTestResults={loadAgentTestResults}
                            defects={defects}
                            fetchDefects={fetchDefects}
                            lastDataRefresh={lastDataRefresh}
                            setLastDataRefresh={setLastDataRefresh}
                            currentScope={currentScope}
                            onStatusScopeChange={setCurrentScope}
                            onStatusAPISelect={setSelectedAPI}
                            onStatusApplicationSelect={setSelectedApplication}
                        />
                    )}
                </main>


                {/* MODULAR COMPONENTS - All modals now use separate components 
                <AIConfigModal
                    isOpen={showApiKeyModal}
                    onClose={() => setShowApiKeyModal(false)}
                    aiConfig={aiConfig}
                    testwerkApiKey={testwerkApiKey}
                    setTestwerkApiKey={setTestwerkApiKey}
                    agentApiKey={agentApiKey}
                    setAgentApiKey={setAgentApiKey}
                    selectedModel={selectedModel}
                    setSelectedModel={setSelectedModel}
                    onUpdateConfig={handleUpdateAIConfig}
                /> */}

                <ExecuteModal
                    isOpen={isExecuteModalOpen}
                    onClose={() => setIsExecuteModalOpen(false)}
                    selectedAgent={selectedAgent}
                    testCases={testCases}
                    selectedTestsForRun={selectedTestsForRun}
                    setSelectedTestsForRun={setSelectedTestsForRun}
                    results={results}
                    isTestRunning={isTestRunning}
                    aiConfig={aiConfig}
                    onRunTests={handleRunSelectedTests}
                    onSelectAllTests={handleSelectAllTests}
                    onSelectSingleTest={handleSingleTestSelect}
                    onOpenApiConfig={() => setShowApiKeyModal(true)}
                />

                <TestDetailsModal
                    isOpen={isDetailsModalOpen}
                    onClose={handleDetailsModalClose}
                    testDetails={currentTestDetails}
                    isEditMode={isEditMode}
                    setIsEditMode={setIsEditMode}
                    editedTestDetails={editedTestDetails}
                    setEditedTestDetails={setEditedTestDetails}
                    isSaving={isSaving}
                    onSaveChanges={saveTestCaseChanges}
                />

                <DataEditorModal
                    isOpen={isDataModalOpen}
                    onClose={() => setIsDataModalOpen(false)}
                    editableData={editableData}
                    setEditableData={setEditableData}
                    onSaveChanges={handleSaveChanges}
                />

                <DbViewerModal
                    isOpen={isDbViewerModalOpen}
                    onClose={() => setIsDbViewerModalOpen(false)}
                    dbTableData={dbTableData}
                    dbTableHeaders={dbTableHeaders}
                    filteredDbData={filteredDbData}
                    dbFilter={dbFilter}
                    setDbFilter={setDbFilter}
                    isDbEditMode={isDbEditMode}
                    setIsDbEditMode={setIsDbEditMode}
                    selectedDbRows={selectedDbRows}
                    onSelectDbRow={handleSelectDbRow}
                    onSelectAllDbRows={handleSelectAllDbRows}
                    onBulkDelete={handleBulkDelete}
                    onBulkDuplicate={handleBulkDuplicate}
                    onEditSelectedDbRow={handleEditSelectedDbRow}
                    onDbFieldEdit={handleDbFieldEdit}
                    setDbTableData={setDbTableData}
                    fetchTestCases={fetchTestCases}
                    setDbTableHeaders={setDbTableHeaders}
                />

                {/* Agent Management Modals */}
                <AgentUploadModal
                    isOpen={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    onUploadComplete={() => {
                        //console.log('Agent upload completed, refreshing agents...');

                        // Dynamic polling until new agent appears (no fixed delays)
                        const currentAgentCount = agents.length;
                        let pollAttempts = 0;
                        const maxPolls = 20; // Maximum 20 attempts (about 60 seconds total)

                        const pollForNewAgent = async () => {
                            pollAttempts++;

                            try {
                                const updatedList = await fetchIntelligentAgents(); //get fresh list

                                // Success condition: agent count increased
                                if (updatedList.length > currentAgentCount) {        //check fresh list
                                    //console.log(`New agent detected! (${updatedList.length} total, attempt ${pollAttempts})`);
                                    setAgents(updatedList); //ensure state is updated
                                    return; // Stop polling
                                }

                                // Continue polling if agent count hasn't increased yet
                                if (pollAttempts < maxPolls) {
                                    //console.log(`Processing... (${updatedList.length}/${currentAgentCount + 1} agents, attempt ${pollAttempts})`);
                                    const delay = Math.min(500 * pollAttempts, 8000);
                                    setTimeout(pollForNewAgent, delay);
                                } else {
                                    console.warn('⚠️ Max polling attempts reached - agent may have uploaded but not detected');
                                }

                            } catch (error) {
                                console.warn(`⚠️ Polling attempt ${pollAttempts} failed:`, error);
                                if (pollAttempts < maxPolls) {
                                    setTimeout(pollForNewAgent, 2000);
                                }
                            }
                        };


                        // Start polling immediately (no initial delay)
                        pollForNewAgent();
                        setShowUploadModal(false);
                    }}
                    setIsUploading={setIsUploading}
                />

                <TemplateLibraryModal
                    isOpen={showTemplateLibrary}
                    onClose={() => setShowTemplateLibrary(false)}
                    onUseTemplate={(template) => {
                        alert(`Using template: ${template.name}`);
                        setShowTemplateLibrary(false);
                    }}
                />

                {/* Analytics Dashboard Modal - Phase 4 */}
                <AnalyticsDashboardModal
                    isOpen={isAnalyticsDashboardOpen}
                    onClose={() => setIsAnalyticsDashboardOpen(false)}
                    aiConfig={aiConfig}
                    agents={agents}
                    results={results}
                    selectedAgent={selectedAgent}
                />




                {isEnvironmentPopupOpen && (
                    <div className="admin-console-overlay" onClick={() => setIsEnvironmentPopupOpen(false)} style={{ zIndex: 10000 }}>
                        <div className="admin-console-modal largeX" onClick={(e) => e.stopPropagation()} style={{ zIndex: 10001 }}>
                            <div className="admin-console-header">
                                <h2>Environment Status</h2>
                                <button
                                    className="close-button"
                                    onClick={() => setIsEnvironmentPopupOpen(false)}
                                >✕</button>
                            </div>
                            <EnvironmentWidget status={status} aiConfig={aiConfig} />
                        </div>
                    </div>
                )}


                {/* System Resources Popup */}
                {isResourcesPopupOpen && (
                    <div className="admin-console-overlay" onClick={() => setIsResourcesPopupOpen(false)} style={{ zIndex: 10000 }}>
                        <div className="admin-console-modal largeX" onClick={(e) => e.stopPropagation()} style={{ zIndex: 10001 }}>
                            <div className="admin-console-header">
                                <h2>System HW/SW Resources</h2>
                                <button
                                    className="close-button"
                                    onClick={() => setIsResourcesPopupOpen(false)}
                                >×</button>
                            </div>
                            <EnvironmentWidget
                                status={null}
                                aiConfig={null}
                                mode="resources"
                                apiBaseUrl={API_BASE_URL}
                            />
                        </div>
                    </div>
                )}

                {/* Legal Footer */}
                <footer className="alpha-footer">
                    <div className="legal-links">
                        <button
                            className="legal-link"
                            onClick={handleHomeClick}
                        >
                            Home
                        </button>
                        <button
                            className="legal-link"
                            onClick={() => handleLegalClick('privacy')}
                        >
                            Privacy Policy
                        </button>
                        <button
                            className="legal-link"
                            onClick={() => handleLegalClick('imprint')}
                        >
                            Imprint
                        </button>
                        <button
                            className="legal-link"
                            onClick={() => handleLegalClick('contact')}
                        >
                            Contact
                        </button>
                    </div>
                    <div className="alpha-notice">
                        <span>TestWerk AI Platform </span>
                        <span className="version-footer">{VERSION_INFO.fullVersion}</span>
                    </div>
                </footer>

                {isAdminPopupOpen && (
                    <div style={{ position: 'fixed', zIndex: 10000 }}>
                        <AdminConsole onClose={() => setIsAdminPopupOpen(false)} />
                    </div>
                )}


                {/* Settings Drawer */}
                <SettingsDrawer
                    isOpen={isSettingsDrawerOpen}
                    onClose={() => setIsSettingsDrawerOpen(false)}
                    apiBaseUrl={API_BASE_URL}
                    aiConfig={aiConfig}
                    aiMode={aiMode}
                />

                {/* Legal Content Popup */}
                {isLegalPopupOpen && (
                    <div className="modal-overlay" onClick={() => setIsLegalPopupOpen(false)}>
                        <div className="modal-content legal-popup" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{legalContent.title}</h2>
                                <button
                                    className="close-button"
                                    onClick={() => setIsLegalPopupOpen(false)}
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="legal-popup-content">
                                {isLoadingLegal ? (
                                    <div className="loading-spinner">Loading...</div>
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: legalContent.content }} />
                                )}
                            </div>
                            <div className="modal-actions">
                                <button onClick={() => setIsLegalPopupOpen(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                {/* Logs Modal */}
                {isLogsModalOpen && (
                    <div className="admin-console-overlay" onClick={() => setIsLogsModalOpen(false)} style={{ zIndex: 10000 }}>
                        <div className="admin-console-modal largeX" onClick={(e) => e.stopPropagation()} style={{ zIndex: 10001, maxHeight: '85vh' }}>
                            <div className="admin-console-header">
                                <h2>System Logs</h2>
                                <button
                                    className="close-button"
                                    onClick={() => setIsLogsModalOpen(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="admin-content" style={{ maxHeight: 'calc(85vh - 80px)' }}>
                                {/* Log Filters with All checkbox */}
                                <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {/* All checkbox */}
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginRight: '1rem', fontWeight: 'bold' }}>
                                        <input
                                            type="checkbox"
                                            checked={Object.values(logFilters).every(v => v === true)}
                                            onChange={(e) => {
                                                const newValue = e.target.checked;
                                                setLogFilters(prev =>
                                                    Object.keys(prev).reduce((acc, key) => ({
                                                        ...acc,
                                                        [key]: newValue
                                                    }), {} as typeof logFilters)
                                                );
                                            }}
                                        />
                                        <span style={{ fontSize: '0.75rem' }}>ALL</span>
                                    </label>

                                    <div style={{ width: '1px', height: '20px', backgroundColor: '#ddd' }} />

                                    {/* Individual service checkboxes */}
                                    {Object.entries(logFilters).map(([service, enabled]) => (
                                        <label key={service} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={enabled}
                                                onChange={(e) => setLogFilters(prev => ({ ...prev, [service]: e.target.checked }))}
                                            />
                                            <span style={{ fontSize: '0.7rem' }}>{service}</span>
                                        </label>
                                    ))}
                                </div>

                                {/* Logs Display */}
                                <div style={{
                                    backgroundColor: '#1e1e1e',
                                    color: '#04ed3aff',
                                    padding: '1rem',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    whiteSpace: 'pre-wrap',
                                    height: 'calc(85vh - 200px)',
                                    overflow: 'auto'
                                }}>
                                    {filteredLogs.length > 0 ? (
                                        filteredLogs.map((log, index) => (
                                            <div key={index}>{log}</div>
                                        ))
                                    ) : (
                                        <div style={{ color: '#666' }}>No logs to display</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}


            </div>
        </Router>
    );
}

export default App;